/**
 * Gamepad mapping of the game and the shell, by the W3C "standard" layout (an Xbox pad's
 * names; a PlayStation pad has its buttons in the same places). Pads without the standard
 * layout get the same button numbers, best effort.
 *
 * In a race: A, D-pad up and RT jump (holding works, like holding the screen or Space), X or
 * LB use boost slot 1, Y or RB slot 2, Start opens and closes the menu, B closes it, Back
 * (Select, Share) mutes the microphone. Spectators switch the watched racer with the D-pad's
 * left / right and with LB / RB (they have no boosts to use).
 * In the in-game menu and everywhere outside the race the D-pad and the left stick move the
 * focus (focusNav.ts), A presses the focused control and B goes back, like Escape.
 */
import type { Direction } from './focusNav';
import type { KeyCommand } from './keys';

/** Buttons of the standard layout, and the left stick read as four more (navigation only). */
export const PAD = {
  A: 0,
  B: 1,
  X: 2,
  Y: 3,
  LB: 4,
  RB: 5,
  LT: 6,
  RT: 7,
  BACK: 8,
  START: 9,
  UP: 12,
  DOWN: 13,
  LEFT: 14,
  RIGHT: 15,
  STICK_UP: 100,
  STICK_DOWN: 101,
  STICK_LEFT: 102,
  STICK_RIGHT: 103,
} as const;

/** What a pad does in a race: the keyboard's commands, plus B (back: closes the menu). */
export type PadCommand = KeyCommand | { kind: 'back' };

const JUMP: PadCommand = { kind: 'jump' };
const MENU: PadCommand = { kind: 'menu' };
const MUTE: PadCommand = { kind: 'mute' };
const BACK: PadCommand = { kind: 'back' };
const BOOST_1: PadCommand = { kind: 'boost', slot: 0 };
const BOOST_2: PadCommand = { kind: 'boost', slot: 1 };
const PREVIOUS: PadCommand = { kind: 'switch', step: -1 };
const NEXT: PadCommand = { kind: 'switch', step: 1 };
const NONE: readonly PadCommand[] = [];

const BY_BUTTON: Readonly<Record<number, readonly PadCommand[]>> = {
  [PAD.A]: [JUMP],
  [PAD.RT]: [JUMP],
  [PAD.UP]: [JUMP],
  [PAD.X]: [BOOST_1],
  [PAD.Y]: [BOOST_2],
  // Racers never switch and spectators have no boosts: the bumpers do both.
  [PAD.LB]: [BOOST_1, PREVIOUS],
  [PAD.RB]: [BOOST_2, NEXT],
  [PAD.LEFT]: [PREVIOUS],
  [PAD.RIGHT]: [NEXT],
  [PAD.START]: [MENU],
  [PAD.B]: [BACK],
  [PAD.BACK]: [MUTE],
};

const DIRECTIONS: Readonly<Record<number, Direction>> = {
  [PAD.UP]: 'up',
  [PAD.DOWN]: 'down',
  [PAD.LEFT]: 'left',
  [PAD.RIGHT]: 'right',
  [PAD.STICK_UP]: 'up',
  [PAD.STICK_DOWN]: 'down',
  [PAD.STICK_LEFT]: 'left',
  [PAD.STICK_RIGHT]: 'right',
};

/** What a button does in a race (nothing: empty). */
export function padCommands(button: number): readonly PadCommand[] {
  return BY_BUTTON[button] ?? NONE;
}

/** The way a D-pad button or the left stick moves the focus (null: not a direction). */
export function padDirection(button: number): Direction | null {
  return DIRECTIONS[button] ?? null;
}

/** How a pad names its buttons: Xbox letters, or PlayStation symbols in the same places. */
export type PadStyle = 'xbox' | 'playstation';

/** Sony's vendor id or a PlayStation pad's name, as Chrome, Firefox and Safari report them. */
const PLAYSTATION = /054c|playstation|dualshock|dualsense|sony/i;

export function padStyle(id: string): PadStyle {
  return PLAYSTATION.test(id) ? 'playstation' : 'xbox';
}
